import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current > 12) {
          setStatus("error");
          return;
        }
      } catch {
        if (attempts.current > 12) { setStatus("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isCancel, location.search, refreshUser]);

  return (
    <div className="min-h-screen grid-bg bg-background flex items-center justify-center p-6">
      <div className="glass rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-result">
        {status === "checking" && (
          <><Loader2 className="animate-spin text-primary mx-auto mb-4" size={40} /><h1 className="font-display text-2xl font-bold">Confirming payment…</h1><p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p></>
        )}
        {status === "paid" && (
          <><CheckCircle2 className="text-primary mx-auto mb-4" size={48} /><h1 className="font-display text-2xl font-bold">Payment successful</h1><p className="text-muted-foreground mt-2">Your credits and plan have been updated.</p><Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/dashboard")} data-testid="back-to-studio">Back to Studio</Button></>
        )}
        {status === "cancelled" && (
          <><XCircle className="text-muted-foreground mx-auto mb-4" size={48} /><h1 className="font-display text-2xl font-bold">Checkout cancelled</h1><p className="text-muted-foreground mt-2">No charge was made.</p><Button variant="outline" className="mt-6 border-border" onClick={() => navigate("/dashboard/billing")} data-testid="back-to-billing">Back to Billing</Button></>
        )}
        {status === "error" && (
          <><XCircle className="text-destructive mx-auto mb-4" size={48} /><h1 className="font-display text-2xl font-bold">Something went wrong</h1><p className="text-muted-foreground mt-2">We couldn't confirm your payment.</p><Button variant="outline" className="mt-6 border-border" onClick={() => navigate("/dashboard/billing")} data-testid="back-to-billing">Back to Billing</Button></>
        )}
      </div>
    </div>
  );
}
